/**
 * Passenger checkout — hotel customer-facing display SSOT.
 * When agent HR booking SSOT is active, bookingSsotIls wins over stale pricingSnapshot /
 * displayCurrencies.ils / ourPrice.PWCCF (supplier quote).
 */

import { roundMoney2 } from "../pricing/pricingEngine";
import { resolveCustomerFacingHotelLineIlsFromOrder } from "./hotelBookingPriceSsot";
import { resolveHotelAgentHandlingFeeIls } from "./resolveHotelAgentOfferDisplayTotal";
import { formatHotelIlsRoundedDisplay } from "./formatHotelIlsRoundedDisplay";

export const PASSENGER_PRICE_SOURCE_TRACE = "[PASSENGER_PRICE_SOURCE_TRACE]";
export const PASSENGER_LEFT_SIDEBAR_PRICE_TRACE = "[PASSENGER_LEFT_SIDEBAR_PRICE_TRACE]";

function finitePositive(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * bookingSsotIls locked at room select exceeds stale supplier-line snapshot (HR markup drift).
 * @param {object|null|undefined} order
 */
export function hasAgentHrBookingSsotDrift(order) {
  if (!order || typeof order !== "object") return false;
  const bookingSsot = resolveCustomerFacingHotelLineIlsFromOrder(order);
  const b = finitePositive(bookingSsot);
  if (b == null) return false;

  const pkg = order.selectedHotelPackage;
  const staleCandidates = [
    order.pricingSnapshot?.hotelTotalIls,
    order.selectedPriceSnapshot?.pricingSnapshot?.hotelTotalIls,
    pkg?.displayCurrencies?.ils,
    order.ourPrice?.supplierNet,
    pkg?.ourPrice?.supplierNet,
  ];

  return staleCandidates.some((raw) => {
    const s = finitePositive(raw);
    return s != null && b > s + 0.5;
  });
}

/** @param {object|null|undefined} order */
export function isAgentHrCustomerFacingSsotOrder(order) {
  if (!order || typeof order !== "object") return false;

  const trace =
    order.bookingSsotTrace ??
    order.selectedPriceSnapshot?.bookingSsotTrace ??
    null;
  const pipeline = String(trace?.pipeline ?? trace?.source ?? "");
  if (pipeline.includes("agent_hr")) return true;

  const pricingSources = [
    order.agentOurPrice?.pricingSource,
    order.ourPrice?.pricingSource,
    order.selectedHotelPackage?.ourPrice?.pricingSource,
  ];
  if (pricingSources.some((s) => s === "agent_hotel_customer_facing_ssot")) {
    return true;
  }

  return hasAgentHrBookingSsotDrift(order);
}

/**
 * @param {object|null|undefined} order
 * @param {{
 *   displayedHotelAmount?: number|null,
 *   sourceResolver?: string|null,
 *   totalWithAgentFeeIls?: number|null,
 * }} [opts]
 */
export function buildPassengerPriceSourceTracePayload(order, opts = {}) {
  const pkg =
    order?.selectedHotelPackage && typeof order.selectedHotelPackage === "object"
      ? order.selectedHotelPackage
      : null;
  const pricingSnapshot =
    order?.pricingSnapshot ??
    order?.selectedPriceSnapshot?.pricingSnapshot ??
    null;
  const ourPrice = order?.ourPrice ?? pkg?.ourPrice ?? {};
  const dcRaw = pkg?.displayCurrencies?.ils;
  const dcIls =
    dcRaw != null && dcRaw !== "" && Number.isFinite(Number(dcRaw))
      ? Number(dcRaw)
      : null;

  return {
    displayedHotelAmount: opts.displayedHotelAmount ?? null,
    bookingSsotIls: resolveCustomerFacingHotelLineIlsFromOrder(order),
    "pricingSnapshot.hotelTotalIls": pricingSnapshot?.hotelTotalIls ?? null,
    "ourPrice.priceWithCommission": ourPrice?.priceWithCommission ?? null,
    "displayCurrencies.ils": dcIls,
    supplierNet: ourPrice?.supplierNet ?? pricingSnapshot?.supplierAmount ?? null,
    sourceResolver: opts.sourceResolver ?? null,
    agentHrActive: isAgentHrCustomerFacingSsotOrder(order),
    totalWithAgentFeeIls: opts.totalWithAgentFeeIls ?? null,
  };
}

/** @param {Record<string, unknown>} payload */
export function logPassengerPriceSourceTrace(payload) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;
  try {
    // eslint-disable-next-line no-console
    console.info(PASSENGER_PRICE_SOURCE_TRACE, payload);
  } catch {
    /* ignore */
  }
}

/**
 * Agent HR passenger total — bookingSsotIls + agent handling fee (+ optional extras).
 * @param {{
 *   currentHotelOrder: object|null,
 *   usdToIls?: (usd: number, fixed?: boolean) => number,
 *   extrasIls?: number,
 * }} opts
 */
export function resolvePassengerHotelTotalWithAgentFeeIls(opts) {
  const order = opts.currentHotelOrder;
  const agentHrActive = isAgentHrCustomerFacingSsotOrder(order);
  const bookingSsotIls = resolveCustomerFacingHotelLineIlsFromOrder(order);

  if (!agentHrActive || bookingSsotIls == null) {
    return {
      ok: false,
      agentHrActive,
      hotelTotalIls: null,
      agentFeeIls: 0,
      totalWithAgentFeeIls: null,
      sourceResolver: null,
    };
  }

  const ourPrice = order?.ourPrice ?? order?.selectedHotelPackage?.ourPrice ?? {};
  const hotelTotalIls = roundMoney2(bookingSsotIls);
  const agentFeeIls = resolveHotelAgentHandlingFeeIls(
    ourPrice,
    hotelTotalIls,
    opts.usdToIls
  );
  const extras = Math.max(0, Math.ceil(Number(opts.extrasIls) || 0));
  const totalWithAgentFeeIls = Math.ceil(hotelTotalIls + agentFeeIls + extras);

  return {
    ok: true,
    agentHrActive: true,
    hotelTotalIls,
    agentFeeIls,
    totalWithAgentFeeIls,
    hotelIlsFormatted: formatHotelIlsRoundedDisplay(hotelTotalIls),
    totalWithAgentFeeFormatted: formatHotelIlsRoundedDisplay(totalWithAgentFeeIls),
    sourceResolver: "bookingSsotIls",
  };
}

/**
 * @param {object|null|undefined} order
 * @param {{
 *   componentName: string,
 *   displayedAmount?: number|null,
 *   sourceResolver?: string|null,
 * }} opts
 */
export function buildPassengerLeftSidebarPriceTracePayload(order, opts) {
  const pkg =
    order?.selectedHotelPackage && typeof order.selectedHotelPackage === "object"
      ? order.selectedHotelPackage
      : null;
  const pricingSnapshot =
    order?.pricingSnapshot ??
    order?.selectedPriceSnapshot?.pricingSnapshot ??
    null;
  const ourPrice = order?.ourPrice ?? pkg?.ourPrice ?? {};
  const bookingSsotIls = resolveCustomerFacingHotelLineIlsFromOrder(order);
  const agentHrActive = isAgentHrCustomerFacingSsotOrder(order);
  const displayedAmount = finitePositive(opts.displayedAmount);
  const expectedAmount =
    bookingSsotIls != null ? Math.ceil(roundMoney2(bookingSsotIls)) : null;

  let status = "SKIP";
  if (agentHrActive && bookingSsotIls != null && displayedAmount != null) {
    status =
      Math.abs(displayedAmount - expectedAmount) <= 1 &&
      opts.sourceResolver === "bookingSsotIls"
        ? "PASS"
        : "FAIL";
  }

  return {
    componentName: opts.componentName,
    displayedAmount,
    bookingSsotIls,
    "pricingSnapshot.hotelTotalIls": pricingSnapshot?.hotelTotalIls ?? null,
    "ourPrice.priceWithCommission": ourPrice?.priceWithCommission ?? null,
    "displayCurrencies.ils": finitePositive(pkg?.displayCurrencies?.ils),
    sourceResolver: opts.sourceResolver ?? null,
    agentHrActive,
    status,
  };
}

/** @param {Record<string, unknown>} payload */
export function logPassengerLeftSidebarPriceTrace(payload) {
  if (typeof import.meta !== "undefined" && !import.meta.env?.DEV) return;
  try {
    // eslint-disable-next-line no-console
    console.info(PASSENGER_LEFT_SIDEBAR_PRICE_TRACE, payload);
    if (payload.status === "FAIL") {
      // eslint-disable-next-line no-console
      console.error(`${PASSENGER_LEFT_SIDEBAR_PRICE_TRACE}:FAIL`, payload);
    }
  } catch {
    /* ignore */
  }
}
